import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Star, Flame, Users, Wine, ChevronRight } from 'lucide-react';
import useSEO from '@/hooks/useSEO';

const CHEF_IMAGE = "https://customer-assets.emergentagent.com/job_bcf6ecb8-480f-48a0-b555-9c0d6414b56f/artifacts/sy3v37j1_Capture%20d%27%C3%A9cran%202026-02-19%20173007.png";
const EXTERIOR_IMAGE = "https://customer-assets.emergentagent.com/job_bcf6ecb8-480f-48a0-b555-9c0d6414b56f/artifacts/5nv0ba1o_Capture%20d%27%C3%A9cran%202026-02-19%20172943.png";
const GALLERY_IMAGE = "https://customer-assets.emergentagent.com/job_bcf6ecb8-480f-48a0-b555-9c0d6414b56f/artifacts/a58le5w7_Capture%20d%27%C3%A9cran%202026-02-19%20172040.png";

const Restaurant = () => {
  useSEO({
    title: 'Le Restaurant — NotaBene Sympho à Mons',
    description: 'Découvrez l\'ambiance chaleureuse de NotaBene Sympho à Mons. Cuisine italienne authentique, pizzas, pâtes maison. Convivial et familial.',
    canonical: '/restaurant',
  });
  return (
    <div className="pt-20" data-testid="restaurant-page">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[400px] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${EXTERIOR_IMAGE})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1C1917]/90 to-[#1C1917]/50" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[#F59E0B] font-medium tracking-widest uppercase mb-4">
            Bienvenue chez nous
          </p>
          <h1 className="font-['Playfair_Display'] text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6">
            Le Restaurant
          </h1>
          <p className="text-white/80 text-lg max-w-xl">
            Une ambiance chaleureuse, une cuisine généreuse et un accueil familial 
            depuis plus de 20 ans à Mons.
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 md:py-32 bg-[#F5F2EA]" data-testid="story-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="text-[#C62E2E] font-medium tracking-widest uppercase mb-4">
                Notre Histoire
              </p>
              <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-[#1C1917] mb-6">
                L'authenticité italienne au cœur de Mons
              </h2>
              <div className="space-y-6 text-[#57534E] text-lg leading-relaxed">
                <p>
                  NotaBene Sympho, c'est avant tout une histoire de passion. Depuis plus de 20 ans, 
                  nous perpétuons la tradition culinaire italienne avec des recettes transmises 
                  de génération en génération.
                </p>
                <p>
                  Notre four à bois traditionnel est le cœur de notre cuisine. C'est lui qui donne 
                  à nos pizzas cette texture unique : une pâte légèrement croustillante à l'extérieur, 
                  moelleuse à l'intérieur, avec ce parfum de bois brûlé si caractéristique.
                </p>
                <p>
                  Chaque jour, nos pâtes sont préparées fraîchement, nos sauces mijotent doucement, 
                  et nos ingrédients sont sélectionnés avec soin. C'est notre promesse de qualité.
                </p>
              </div>
            </div>
            
            <div className="relative">
              <img
                src={CHEF_IMAGE}
                alt="Chef Ernesto au restaurant"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 md:py-32 bg-white" data-testid="features-grid-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#C62E2E] font-medium tracking-widest uppercase mb-4">
              Ce qui nous distingue
            </p>
            <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-[#1C1917]">
              Notre engagement qualité
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-[#C62E2E]/10 flex items-center justify-center mx-auto mb-6">
                <Flame className="w-10 h-10 text-[#C62E2E]" />
              </div>
              <h3 className="font-['Playfair_Display'] text-xl font-semibold mb-3">Four à Bois</h3>
              <p className="text-[#57534E]">
                Cuisson traditionnelle pour des pizzas parfaites
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-[#C62E2E]/10 flex items-center justify-center mx-auto mb-6">
                <Star className="w-10 h-10 text-[#C62E2E]" />
              </div>
              <h3 className="font-['Playfair_Display'] text-xl font-semibold mb-3">43 Pizzas</h3>
              <p className="text-[#57534E]">
                Le plus grand choix de la région
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-[#C62E2E]/10 flex items-center justify-center mx-auto mb-6">
                <Users className="w-10 h-10 text-[#C62E2E]" />
              </div>
              <h3 className="font-['Playfair_Display'] text-xl font-semibold mb-3">Pizzas Géantes</h3>
              <p className="text-[#57534E]">
                Portions généreuses pour groupes et familles
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-[#C62E2E]/10 flex items-center justify-center mx-auto mb-6">
                <Wine className="w-10 h-10 text-[#C62E2E]" />
              </div>
              <h3 className="font-['Playfair_Display'] text-xl font-semibold mb-3">Pâtes Fraîches</h3>
              <p className="text-[#57534E]">
                Préparées maison chaque jour
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Menu Info Section */}
      <section className="py-20 md:py-32 bg-[#1C1917]" data-testid="menu-info-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#F59E0B] font-medium tracking-widest uppercase mb-4">
                Notre Carte
              </p>
              <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-white mb-6">
                Pizzas classiques & géantes
              </h2>
              <div className="space-y-6 text-white/80 text-lg leading-relaxed">
                <p>
                  Toutes nos pizzas sont disponibles en deux formats : <strong className="text-white">classique</strong> pour 
                  une personne, ou <strong className="text-white">géante</strong> à partager entre 2 à 4 personnes.
                </p>
                <p>
                  Les pizzas géantes sont idéales pour les soirées entre amis, les repas en famille, 
                  ou simplement pour ceux qui ont un grand appétit !
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-6 mt-10">
                <div className="bg-white/10 rounded-xl p-6">
                  <p className="text-[#F59E0B] font-['Playfair_Display'] text-3xl font-bold">43</p>
                  <p className="text-white/80">Pizzas différentes</p>
                </div>
                <div className="bg-white/10 rounded-xl p-6">
                  <p className="text-[#F59E0B] font-['Playfair_Display'] text-3xl font-bold">18</p>
                  <p className="text-white/80">Pâtes maison</p>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <img
                  src="https://images.pexels.com/photos/3343622/pexels-photo-3343622.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Pizza au four"
                  className="rounded-xl w-full aspect-square object-cover"
                />
                <img
                  src="https://images.unsplash.com/photo-1649675602947-81a41c1ba4af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHw0fHxjaGVmJTIwaGFuZHMlMjBrbmVhZGluZyUyMHBpenphJTIwZG91Z2glMjBmbG91cnxlbnwwfHx8fDE3NzE1MTkwMzZ8MA&ixlib=rb-4.1.0&q=85"
                  alt="Pâtes fraîches"
                  className="rounded-xl w-full aspect-[4/3] object-cover"
                />
              </div>
              <div className="space-y-4 pt-8">
                <img
                  src="https://images.unsplash.com/photo-1770670644176-100813bce5a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxpdGFsaWFuJTIwcGl6emElMjB3b29kJTIwZmlyZSUyMG92ZW4lMjBjbG9zZSUyMHVwfGVufDB8fHx8MTc3MTUxOTEwMXww&ixlib=rb-4.1.0&q=85"
                  alt="Four à bois"
                  className="rounded-xl w-full aspect-[4/3] object-cover"
                />
                <img
                  src="https://images.pexels.com/photos/29626982/pexels-photo-29626982.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Pizza prête"
                  className="rounded-xl w-full aspect-square object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 md:py-32 bg-[#F5F2EA]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#C62E2E] font-medium tracking-widest uppercase mb-4">
              Nos Services
            </p>
            <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-[#1C1917]">
              Comment nous déguster
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
              <div className="text-5xl mb-6">🍽️</div>
              <h3 className="font-['Playfair_Display'] text-2xl font-semibold mb-3">Sur Place</h3>
              <p className="text-[#57534E] mb-6">
                Profitez de notre ambiance chaleureuse dans notre salle de restaurant
              </p>
              <Link to="/reservation">
                <Button variant="outline" className="rounded-full">
                  Réserver une table
                </Button>
              </Link>
            </div>
            
            <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
              <div className="text-5xl mb-6">🥡</div>
              <h3 className="font-['Playfair_Display'] text-2xl font-semibold mb-3">À Emporter</h3>
              <p className="text-[#57534E] mb-6">
                Commandez et récupérez vos plats directement au restaurant
              </p>
              <Link to="/commander">
                <Button variant="outline" className="rounded-full">
                  Commander
                </Button>
              </Link>
            </div>
            
            <div className="bg-white rounded-2xl p-8 text-center shadow-sm">
              <div className="text-5xl mb-6">🚗</div>
              <h3 className="font-['Playfair_Display'] text-2xl font-semibold mb-3">Livraison</h3>
              <p className="text-[#57534E] mb-6">
                Nous livrons vos plats chauds directement chez vous
              </p>
              <Link to="/commander">
                <Button variant="outline" className="rounded-full">
                  Se faire livrer
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#C62E2E] font-medium tracking-widest uppercase mb-4">
              Galerie
            </p>
            <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-[#1C1917]">
              En images
            </h2>
          </div>
          
          <div className="rounded-2xl overflow-hidden shadow-xl">
            <img
              src={GALLERY_IMAGE}
              alt="Galerie NotaBene Sympho"
              className="w-full"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-[#C62E2E]" data-testid="restaurant-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-white mb-6">
            Venez nous rendre visite
          </h2>
          <p className="text-white/90 text-lg mb-10 max-w-2xl mx-auto">
            Nous sommes ouverts du mercredi au dimanche, midi et soir. 
            Réservez votre table ou passez nous voir !
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/reservation">
              <Button
                className="bg-white text-[#C62E2E] hover:bg-white/90 rounded-full px-10 py-6 text-lg font-semibold"
                data-testid="restaurant-reserve-btn"
              >
                Réserver une table
              </Button>
            </Link>
            <a href="tel:+3265950649">
              <Button
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#C62E2E] rounded-full px-10 py-6 text-lg font-semibold"
              >
                Appeler: 065 95 06 49
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Restaurant;
